import { ref, watch, type Ref } from "vue"
import type { GridApi, IRowNode } from "ag-grid-community"

/**
 * Keeps a reactive list of the rows currently visible in an ag-grid after its
 * filters have been applied, so charts and totals can reflect the same subset
 * of data the user sees in the table.
 *
 * Falls back to the full source list until the grid is ready, so the first
 * render is never empty.
 */
export function useGridFilterSync<T>(gridApi: Ref<GridApi | null>, sourceRows: Readonly<Ref<readonly T[]>>) {
	const visibleRows = ref([]) as Ref<T[]>
	const isFiltered = ref(false)

	function refreshVisibleRows() {
		const api = gridApi.value
		if (!api || api.isDestroyed()) {
			visibleRows.value = [...(sourceRows.value ?? [])]
			isFiltered.value = false
			return
		}

		const rows: T[] = []
		api.forEachNodeAfterFilterAndSort((node: IRowNode) => {
			if (node.data != null) rows.push(node.data as T)
		})

		visibleRows.value = rows
		isFiltered.value = api.isAnyFilterPresent()
	}

	/**
	 * Clears every column filter on the grid at once.
	 * The grid raises filter-changed in response, which refreshes the visible rows.
	 */
	function clearAllFilters() {
		const api = gridApi.value
		if (!api || api.isDestroyed()) return
		api.setFilterModel(null)
	}

	// Keep in sync when the underlying data is replaced (e.g. results reloaded).
	watch(sourceRows, refreshVisibleRows, { immediate: true, deep: false })

	return { visibleRows, isFiltered, refreshVisibleRows, clearAllFilters }
}
